import type { Direction } from "../../../engine/direction";
import { directionDelta } from "../../../engine/direction";
import type { RGBColor } from "../interpolation";
import {
	colorConstant,
	hold,
	lerp,
	parabolic,
	randomAngle,
	randomBetween,
} from "../interpolation";
import { Particle } from "../particle";

export interface BurstParticleOpts {
	ttl: [number, number];
	size: [number, number];
	color: RGBColor;
	speed: [number, number];
	gravity: number;
	coneHalfAngle?: number;
	/** Fraction of the lifetime at full opacity before fading out (default 0.8). */
	opacityHold?: number;
}

// Half-width of the directional cone in radians (~90 degree spread total)
const CONE_HALF_ANGLE = Math.PI / 4;

export class BurstParticle extends Particle {
	constructor(originX: number, originY: number, opts: BurstParticleOpts, direction?: Direction) {
		let angle: number;
		if (direction) {
			const delta = directionDelta(direction);
			const baseAngle = Math.atan2(delta.y, delta.x);
			const halfAngle = opts.coneHalfAngle ?? CONE_HALF_ANGLE;
			angle = baseAngle + randomBetween(-halfAngle, halfAngle);
		} else {
			angle = randomAngle();
		}

		const speed = randomBetween(opts.speed[0], opts.speed[1]);
		const startAngle = randomAngle();

		super(originX, originY, {
			ttl: randomBetween(opts.ttl[0], opts.ttl[1]),
			size: lerp(opts.size[0], opts.size[1]),
			opacity: hold(1.0, opts.opacityHold ?? 0.8, lerp(1.0, 0.0)),
			color: colorConstant(opts.color.r, opts.color.g, opts.color.b),
			rotation: lerp(startAngle, startAngle + randomBetween(2, 4)),
			position: parabolic(Math.cos(angle) * speed, Math.sin(angle) * speed, opts.gravity),
		});
	}
}
